import { eq, useLiveQuery } from '@tanstack/react-db'
import { applyAddendums, orderAddendums } from '@/db/application/addendums'
import { activeLogs } from '@/db/application/callouts'

import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import { useDb } from '@/lib/dbContext'
import type { Addendum, AddendumChange, Callout, Customer, Note, StatusChangeLog } from '@/lib/schemas/db'
import { withSpan } from '@/lib/sentry'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import type { InvoiceServiceScreenProps } from '@/screens/InvoiceServiceScreen'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

type InvoiceState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | { kind: 'ready'; screenProps: InvoiceServiceScreenProps }

function logsToNotes(
  logs: Note[] | undefined,
  statusChanges: StatusChangeLog[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
): CalloutNotesSectionNote[] {
  // Soft-deleted notes are no longer part of the record; the audit trail for
  // them lives on the Log Work feed.
  const noteItems: CalloutNotesSectionNote[] = activeLogs(logs ?? []).map((log) =>
    log.photoUrl
      ? {
          id: log.id,
          kind: 'photo',
          authorName: getAuthorDisplayName(log.authorId),
          createdAt: log.createdAt,
          photoUrl: log.photoUrl ?? '',
          photoUrls: log.photoUrls,
          caption: log.caption ?? '',
        }
      : {
          id: log.id,
          kind: 'text',
          authorName: getAuthorDisplayName(log.authorId),
          createdAt: log.createdAt,
          text: log.text ?? '',
        },
  )

  const statusItems: CalloutNotesSectionNote[] = (statusChanges ?? []).map((change) => ({
    id: change.id,
    kind: 'status_change',
    authorName: getAuthorDisplayName(change.authorId),
    createdAt: change.createdAt,
    previousStatus: change.previousStatus,
    newStatus: change.newStatus,
    reason: change.reason,
  }))

  return [...noteItems, ...statusItems].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )
}

export function useSessionInvoiceService(
  sessionIdentity: string,
  onDone: () => void,
): InvoiceState {
  const db = useDb()
  const tryPublicId = looksLikePublicId(sessionIdentity)

  const sessionByIdResult = useLiveQuery(
    (q) =>
      sessionIdentity
        ? q
            .from({ sessions: db.sessionsCollection })
            .where(({ sessions }) => eq(sessions.id, sessionIdentity))
            .findOne()
        : null,
    [sessionIdentity],
  )

  const sessionByPublicIdResult = useLiveQuery(
    (q) =>
      sessionIdentity && tryPublicId
        ? q
            .from({ sessions: db.sessionsCollection })
            .where(({ sessions }) => eq(sessions.publicId, sessionIdentity))
            .findOne()
        : null,
    [sessionIdentity, tryPublicId],
  )

  const session =
    (sessionByIdResult.data as Callout | undefined) ??
    (sessionByPublicIdResult.data as Callout | undefined)
  const sessionId = session?.id ?? sessionIdentity
  if (!tryPublicId && session && (sessionByIdResult.data as Callout | undefined)) {
    captureLegacyIdRoute('session', sessionIdentity)
  }

  const customerResult = useLiveQuery(
    (q) => {
      if (!session?.customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, session.customerId))
        .findOne()
    },
    [session?.customerId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const notesResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ notes: db.sessionNotesCollection })
            .where(({ notes }) => eq(notes.serviceId, sessionId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [sessionId],
  )

  const statusChangesResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ statusChanges: db.sessionStatusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, sessionId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [sessionId],
  )

  const addendumsResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ addendums: db.sessionAddendumsCollection })
            .where(({ addendums }) => eq(addendums.serviceId, sessionId))
            .orderBy(({ addendums }) => addendums.createdAt)
        : null,
    [sessionId],
  )

  // Archived (Invoiced/Cancelled) services aren't in the live sync; pull one
  // in on demand so Invoice Prep loads from a direct link too.
  const recordQueriesSettled =
    !sessionByIdResult.isLoading &&
    !sessionByPublicIdResult.isLoading &&
    !sessionByIdResult.isError &&
    !sessionByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'session',
    sessionIdentity,
    recordQueriesSettled && !session,
  )

  if (
    hydrating ||
    sessionByIdResult.isLoading ||
    sessionByPublicIdResult.isLoading ||
    customerResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    addendumsResult.isLoading
  ) {
    return { kind: 'loading' }
  }

  if (
    sessionByIdResult.isError ||
    sessionByPublicIdResult.isError ||
    customerResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError ||
    addendumsResult.isError
  ) {
    return { kind: 'error' }
  }

  if (!session) return { kind: 'not_found' }

  const addendums = orderAddendums(
    (addendumsResult.data as Addendum[] | undefined) ?? [],
  )
  // The locked record keeps its values; addendums layer on top to give what is
  // in force now — including a corrected Omni invoice ID.
  const effective = applyAddendums(
    session as unknown as Record<string, unknown>,
    addendums,
  ) as unknown as typeof session

  const handleSaveAddendum = async (reason: string, changes: AddendumChange[]) => {
    await db.appendSessionAddendumAndWait(session.id, { reason, changes })
  }

  const customer = customerResult.data as Customer | undefined
  const linkedCustomerName = customer
    ? customer.externalId
      ? `${customer.fullName} – ${customer.externalId}`
      : customer.fullName
    : session.contactName ?? ''

  const notes = logsToNotes(
    notesResult.data as Note[] | undefined,
    statusChangesResult.data as StatusChangeLog[] | undefined,
    db.getAuthorDisplayName,
  )

  const handleMarkInvoiced = async (params: {
    omniInvoiceId?: string
    noOmniInvoice: boolean
    billingOutcome?: 'OmniInvoice' | 'NoOmniInvoice' | 'PosPayment'
  }) => {
    await withSpan(
      {
        op: 'remote-session.status.change',
        name: 'Mark session invoiced',
        attributes: { sessionId: session.id, nextStatus: 'Invoiced' },
      },
      async () => {
        if (session.status !== 'Invoiced') {
          db.appendSessionStatusChange(session.id, {
            previousStatus: session.status,
            newStatus: 'Invoiced',
          })
        }

        await db.sessionsCollection
          .update(session.id, (draft) => {
            draft.status = 'Invoiced'
            draft.invoiceId = params.noOmniInvoice ? undefined : params.omniInvoiceId
            draft.updatedAt = new Date().toISOString()
          })
          .isPersisted.promise

        onDone()
      },
    )
  }

  const handleMoveToReview = async (reason: string) => {
    const normalizedReason = reason.trim()
    if (!normalizedReason) throw new Error('Reason is required when moving a session to review')
    return withSpan(
      {
        op: 'remote-session.status.change',
        name: 'Move session to review from invoice prep',
        attributes: { sessionId: session.id, nextStatus: 'Review' },
      },
      async () => {
        if (session.status !== 'Review') {
          db.appendSessionStatusChange(session.id, {
            previousStatus: session.status,
            newStatus: 'Review',
            reason: normalizedReason,
          })
        }
        await db.sessionsCollection
          .update(session.id, (draft) => {
            draft.status = 'Review'
            draft.updatedAt = new Date().toISOString()
          })
          .isPersisted.promise
      },
    )
  }

  const handleMoveToBooked = async () =>
    withSpan(
      {
        op: 'remote-session.status.change',
        name: 'Move session to booked from invoice prep',
        attributes: { sessionId: session.id, nextStatus: 'Booked' },
      },
      async () => {
        if (session.status !== 'Booked') {
          db.appendSessionStatusChange(session.id, {
            previousStatus: session.status,
            newStatus: 'Booked',
          })
        }
        await db.sessionsCollection
          .update(session.id, (draft) => {
            draft.status = 'Booked'
            draft.updatedAt = new Date().toISOString()
          })
          .isPersisted.promise
      },
    )

  return {
    kind: 'ready',
    screenProps: {
      serviceType: 'Session',
      resourceLabel: session.sessionSubtype ?? 'TeamViewer Session',
      publicId: session.publicId,
      customerName: linkedCustomerName,
      customerId: session.customerId,
      customerPublicId: customer?.publicId,
      visitAddress: effective.contactAddress,
      siteContact: effective.contactName,
      contactPhone: effective.contactPhone,
      contactEmail: effective.contactEmail,
      invoiceTo: effective.invoiceName || effective.contactName || '',
      invoiceEmail: effective.invoiceEmail,
      invoicePhone: effective.invoicePhone,
      invoiceAddress: effective.invoiceAddress,
      serviceInvoice: {
        invoiceName: effective.invoiceName,
        invoiceEmail: effective.invoiceEmail,
        invoicePhone: effective.invoicePhone,
        invoiceAddress: effective.invoiceAddress,
        invoiceVat: effective.invoiceVat,
      },
      customerInvoice: customer
        ? {
            invoiceName: customer.invoiceName,
            invoiceEmail: customer.invoiceEmail,
            invoicePhone: customer.invoicePhone,
            invoiceAddress: customer.invoiceAddress,
            invoiceVat: customer.invoiceVat,
          }
        : undefined,
      invoiceVat: effective.invoiceVat,
      issueSummary: effective.issueSummary ?? '',
      scheduledAt: session.isWholeDay
        ? `${formatRelativeDateTime(session.startTime, { includeTime: false })} · No Time`
        : formatRelativeDateTime(session.startTime),
      calloutStatus: session.status,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      omniCustomerId: customer?.externalId,
      omniInvoiceId: effective.invoiceId,
      notes,
      onMarkInvoiced: handleMarkInvoiced,
      onMoveToReview: handleMoveToReview,
      onMoveToBooked: handleMoveToBooked,
      lockedStatus:
        session.status === 'Invoiced'
          ? 'Invoiced'
          : session.status === 'Cancelled'
            ? 'Cancelled'
            : undefined,
      addendums,
      onSaveAddendum: handleSaveAddendum,
    },
  }
}
